import { message, Form, Input, Switch } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import PhoneNumber from 'components/PhoneNumber';
import FormItem from 'components/FormItem';
import Message from 'components/ui/Message';
import { providerService } from 'services';
import { getFormattedPhoneNumber, getPhoneEXTValidator, getPhoneNumberValidator } from 'utils';
import classNames from 'classnames';

export function CareLocationContact({ application, careFacility, setCareFacility, isEditable = false }) {
  const [form] = Form.useForm();

  const { isEditing, editProps } = useEditCard({
    id: 'care-location-contact',
    onEditStart: () =>
      form.setFieldsValue({
        facilityContactSameAsOrg: careFacility?.facilityContactSameAsOrg,
        facilityContact: careFacility?.facilityContact,
      }),
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const payload = {
        facilityContactSameAsOrg: values.facilityContactSameAsOrg,
        facilityContact: values.facilityContact,
      };
      await providerService.updateCenter(payload, careFacility?.id);
      const updatedCareFacility = await providerService.getCenterById(Number(careFacility?.id));
      setCareFacility(updatedCareFacility);
      form.resetFields();
      message.success('Care location contact updated');
    },
    onDiscard: () => form.resetFields(),
  });

  const facilityContactSameAsOrg = Form.useWatch('facilityContactSameAsOrg', form);
  const showContactSummary = !isEditing && careFacility?.facilityContactSameAsOrg;

  return (
    <Card
      className="mt-8"
      title="Care location contact"
      extra={isEditable ? <CardEdit {...editProps} /> : null}
      noBodyPadding
      stickyHeader
    >
      <Form form={form} disabled={editProps.loading}>
        <TabularList
          indented
          data={[
            ...(showContactSummary
              ? []
              : [
                  {
                    title: 'Same as organization',
                    value: isEditing ? (
                      <Form.Item name="facilityContactSameAsOrg" valuePropName="checked" className="mb-0">
                        <Switch />
                      </Form.Item>
                    ) : careFacility?.facilityContactSameAsOrg ? (
                      'Yes'
                    ) : (
                      'No'
                    ),
                  },
                ]),
            ...(showContactSummary || (isEditing && facilityContactSameAsOrg)
              ? []
              : [
                  {
                    title: 'Contact name',
                    titleRequired: true,
                    value: isEditing ? (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <FormItem
                          name={['facilityContact', 'firstName']}
                          rules={[{ required: true, message: 'First name is required' }]}
                          className="mb-0 flex-1"
                        >
                          <Input placeholder="First name" />
                        </FormItem>

                        <FormItem
                          name={['facilityContact', 'lastName']}
                          rules={[{ required: true, message: 'Last name is required' }]}
                          className="mb-0 flex-1"
                        >
                          <Input placeholder="Last name" />
                        </FormItem>
                      </div>
                    ) : (
                      <>
                        {careFacility?.facilityContact?.firstName} {careFacility?.facilityContact?.lastName}
                      </>
                    ),
                  },
                  {
                    title: 'Position',
                    titleRequired: true,
                    value: isEditing ? (
                      <FormItem
                        name={['facilityContact', 'position']}
                        rules={[{ required: true, message: 'Position is required' }]}
                        className="mb-0"
                      >
                        <Input placeholder="Role/Position" />
                      </FormItem>
                    ) : (
                      careFacility?.facilityContact?.position
                    ),
                  },
                  {
                    title: 'Email',
                    titleRequired: true,
                    value: isEditing ? (
                      <FormItem
                        name={['facilityContact', 'email']}
                        rules={[
                          { required: true, message: 'Email is required' },
                          { type: 'email', message: 'Please enter a valid email' },
                        ]}
                        className="mb-0"
                      >
                        <Input placeholder="Email" />
                      </FormItem>
                    ) : (
                      <a href={`mailto:${careFacility?.facilityContact?.email}`}>
                        {careFacility?.facilityContact?.email}
                      </a>
                    ),
                  },
                  {
                    title: 'Phone',
                    titleRequired: true,
                    value: isEditing ? (
                      <FormItem
                        name={['facilityContact', 'phone']}
                        rules={[
                          { required: true, message: 'Phone number is required' },
                          getPhoneNumberValidator('Invalid phone number'),
                        ]}
                        className="mb-0"
                      >
                        <PhoneNumber />
                      </FormItem>
                    ) : (
                      getFormattedPhoneNumber(careFacility?.facilityContact?.phone)
                    ),
                  },
                  {
                    title: 'Extension',
                    value: isEditing ? (
                      <FormItem
                        name={['facilityContact', 'phoneExt']}
                        rules={[getPhoneEXTValidator('Invalid extension')]}
                        className="mb-0"
                      >
                        <Input placeholder="Extension" />
                      </FormItem>
                    ) : careFacility?.facilityContact?.phoneExt ? (
                      careFacility?.facilityContact?.phoneExt
                    ) : (
                      <span className="italic text-gray-400">Not provided</span>
                    ),
                  },
                ]),
          ]}
        />

        {(showContactSummary || (isEditing && facilityContactSameAsOrg)) && (
          <Message
            title="Using organization's primary contact"
            className={classNames('mx-6 mb-6', isEditing ? '' : 'mt-6')}
          >
            <div className="space-y-1">
              <div>
                {careFacility?.facilityContact?.firstName} {careFacility?.facilityContact?.lastName}
              </div>
              <div className="text-sm text-gray-600">{careFacility?.facilityContact?.position}</div>
              <div className="text-sm">
                <a href={`mailto:${careFacility?.facilityContact?.email}`} className="text-blue-600">
                  {careFacility?.facilityContact?.email}
                </a>
              </div>
              <div className="text-sm">
                {getFormattedPhoneNumber(careFacility?.facilityContact?.phone)}
                {careFacility?.facilityContact?.phoneExt && ` - Ext. ${careFacility?.facilityContact?.phoneExt}`}
              </div>
            </div>
          </Message>
        )}
      </Form>
    </Card>
  );
}
